import { cleanStrings, padId, RIASRecordType } from "~/lib";

export const printCertificate = async (input: any) => {
	"use server";
	// The standard WinAnsi fonts throw on control characters, and legacy rows
	// were saved before cleanStrings ran on write, so scrub again here.
	const certificate = cleanStrings(input);

	certificate.domestic = certificate.scheme?.includes("non-domestic")
		? false
		: true;

	const { PDFDocument, StandardFonts, rgb, degrees } = await import( "pdf-lib" );

	const A4 = { w: 595.28, h: 841.89 };
	const mm = v => (v * 72) / 25.4;
	const xMargin = mm(7);
	const yMargin = mm(10);
	const largeGap = mm(6);
	const lineGap = mm(5);

	const footer = () => {
		page.drawText(`© RIAS-regs ${new Date().getFullYear()}`, {
			x:
				(A4.w -
					bold.widthOfTextAtSize(
						`© RIAS-regs ${new Date().getFullYear()}`,
						9,
					)) /
				2,
			y: mm(8),
			size: 9,
			font: regular,
		});
	};
	const wrapText = (font, text, fontSize, maxWidth) => {
		const words = (text ?? "").toString().split(/\s+/);
		const lines: string[] = [];
		let line = "";
		for (const w of words) {
			const test = line ? line + " " + w : w;
			if (font.widthOfTextAtSize(test, fontSize) <= maxWidth) line = test;
			else {
				if (line) lines.push(line);
				line = w;
			}
		}
		if (line) lines.push(line);
		return lines;
	};

	const ensureSpace = (pdf, page, y, padding = 0) => {
		if (y < yMargin + padding) {
			// Add Continued on next page??
			footer();
			const newPage = pdf.addPage([A4.w, A4.h]);
			return { page: newPage, y: A4.h - yMargin };
		}
		return { page, y };
	};

	const drawLabelValue = (pdf, page, x, y, label, value, opts: Record<string, any> = {}) => {
		const {
			labelSize = 10,
			valueSize = 8,
			labelColor = rgb(0, 0, 0),
			valueColor = rgb(0, 0, 0),
			maxWidth = mm(160),
			gap = 6,
			lineGap = 4,
		} = opts;

		page.drawText(label, {
			x,
			y,
			size: labelSize,
			font: bold,
			color: labelColor,
		});
		const lines = wrapText(regular, value, valueSize, maxWidth);
		let cy = y - labelSize - gap;
		for (const ln of lines) {
			page.drawText(ln, {
				x,
				y: cy,
				size: valueSize,
				font: regular,
				color: valueColor,
			});
			cy -= valueSize + lineGap;
			({ page, y: cy } = ensureSpace(pdf, page, cy));
		}
		return cy + valueSize;
	};

	// Meta boxes
	const metaBox = (y, height) => {
		page.drawRectangle({
			x: xMargin,
			y: y - height,
			width: A4.w - 2 * xMargin,
			height: height,
			borderColor: rgb(0, 0, 0),
			borderWidth: 1,
		});
		return y - height;
	};

	const drawAdminRow = (heading, value, y, lh, lv, change = lineGap) => {
		if (!value) {
			value = "n/a";
		}

		page.drawText(heading, {
			x:
				(A4.w - (lh + lv + mm(5))) / 2 +
				(lh - bold.widthOfTextAtSize(heading, 8)),
			y: y,
			size: 8,
			font: bold,
		});

		const lines = wrapText(regular, value, 9, standardWidth);
		for (const ln of lines) {
			page.drawText(ln, {
				x: (A4.w - (lh + lv + mm(8))) / 2 + lh + mm(5),
				y,
				size: 8,
				font: regular,
			});
			y -= lineGap - mm(2);
		}

		y -= lineGap / 3;
		return y;
	};

	const drawWatermark = () => {
		// --- DRAFT watermark ---
		const { width, height } = page.getSize();
		const text = "DRAFT";
		const fontSize = 250;

		const textWidth = bold.widthOfTextAtSize(text, fontSize);
		const textHeight = bold.heightAtSize(fontSize);

		// page centre
		const cx = width / 2;
		const cy = height / 2;

		// text centre offset (local space)
		const tx = textWidth / 2;
		const ty = textHeight / 2;

		// --- MANUAL ROTATION MATH ---
		// We want the text's centre to appear at the page centre AFTER rotation.
		// So we calculate what (x, y) must be BEFORE rotation.

		const angle = -45 * (Math.PI / 180); // -45° in radians
		const sinA = Math.sin(angle);
		const cosA = Math.cos(angle);

		// rotate (−tx, −ty) to find how far to back-shift the origin
		const rx = -tx * cosA + ty * sinA;
		const ry = -tx * sinA - ty * cosA;

		// final position
		const x = cx + rx;
		const y = cy + ry;

		// draw text
		page.drawText(text, {
			x,
			y,
			size: fontSize,
			font: bold,
			color: rgb(0.85, 0.85, 0.85),
			opacity: 0.5,
			rotate: degrees(-45),
		});
	};

	// --- Create PDF ---
	const pdf = await PDFDocument.create();

	pdf.setTitle(`RIAS Certificate C${padId(certificate.riasId)}`);
	pdf.setAuthor("RIAS-regs");

	let page = pdf.addPage([A4.w, A4.h]);
	const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
	const regular = await pdf.embedFont(StandardFonts.Helvetica);
	const italic = await pdf.embedFont(StandardFonts.HelveticaOblique);

	const standardWidth = A4.w - xMargin * 2 - largeGap * 2;

	// Logo
	const logoBytes = await fetch(
		"https://cdn.golfbusinessnews.com/wp-content/uploads/2025/11/rias-logo-pdf.png",
	).then(r => r.arrayBuffer());
	const logo = await pdf.embedPng(logoBytes);
	// Header Img
	const imgW = (A4.w - 2 * xMargin) * 0.8;
	const imgH = imgW * (221 / 1032);
	let x = (A4.w - imgW) / 2;
	let y = A4.h - yMargin / 2 - imgH;
	page.drawImage(logo, { x, y, width: imgW, height: imgH });
	y -= largeGap;

	//Admin Data
	const schedule1Text = "Schedule 1 Form Attached:";
	const longHeader = bold.widthOfTextAtSize(schedule1Text, 9);

	const nameLines = wrapText(
		regular,
		certificate.projectName,
		9,
		standardWidth - longHeader,
	);

	let longValue = Math.max(
		regular.widthOfTextAtSize(nameLines[0] ?? "", 9),
		regular.widthOfTextAtSize(certificate.meta.buildingWarrant ?? "n/a", 9),
		regular.widthOfTextAtSize(padId(certificate.riasId) ?? "n/a", 9),
	);

	let topY = y;
	y -= lineGap;

	let certName = "C" + padId(certificate.riasId);

	if (certificate.meta.stages?.length) {
		certName += ` - Stage ${certificate.meta.stages.length}`;
	}

	if (certificate.meta.amendments?.length) {
		certName += ` - Amendment ${certificate.meta.amendments.length}`;
	}

	y = drawAdminRow("Certificate Number:", certName, y, longHeader, longValue);
	y = drawAdminRow(
		"Project Name:",
		certificate.projectName,
		y,
		longHeader,
		longValue,
	);
	y = drawAdminRow(
		"Building Warrant No:",
		certificate.meta.buildingWarrant ?? "n/a",
		y,
		longHeader,
		longValue,
	);
	y = drawAdminRow(
		schedule1Text,
		certificate.meta.schedule1Attached,
		y,
		longHeader,
		longValue,
		lineGap,
	);

	metaBox(topY, topY - y);
	y -= largeGap * 1.5;
	({ page, y } = ensureSpace(pdf, page, y));

	// Part A Header
	// PART A
	page.drawText("PART A: To be completed by the Approved Certifier of Design", {
		x: xMargin,
		y,
		size: 11,
		font: bold,
	});
	y -= lineGap / 2;
	topY = y;
	y -= largeGap;

	({ page, y } = ensureSpace(pdf, page, y));

	// Row of columns
	const colW = (standardWidth - mm(8)) / 4;
	const drawCol = (
		lbl: string,
		val: string,
		colIndex: number,
		mWidth: number | null = null,
		cy: number | null = null,
	) => {
		const cx = xMargin + largeGap + colIndex * (colW + mm(5));
		return drawLabelValue(
			pdf,
			page,
			cx,
			cy ? cy - lineGap : y,
			lbl,
			val ?? "",
			{
				maxWidth: mWidth ?? colW,
			},
		);
	};

	const b1 = drawCol("Project Location", certificate.location, 0);
	const b21 = drawCol(
		"Type of Works",
		String(certificate.meta.typeOfWorks ?? ""),
		1,
	);

	// Domestic certificates also state how many dwellings (plots) they cover.
	// Non-domestic ones have no dwellings, so they are left as they were.
	let bPlots = b21;

	if (certificate.domestic) {
		bPlots = drawCol(
			"No. of Dwellings",
			String(certificate.meta.plots?.length ?? 0),
			1,
			colW,
			b21,
		);
	}

	let b22 = drawCol(
		"Compliance Methods",
		certificate.meta.complianceMethods,
		1,
		colW,
		bPlots,
	);

	if (certificate.projectReference) {
		b22 = drawCol(
			"Project Reference",
			certificate.projectReference,
			1,
			colW,
			b22,
		);
	}

	let b3 = drawCol(
		"Description of Works",
		certificate.meta.descriptionOfWorks,
		2,
		2 * colW,
	);

	let b4 = Math.min(b1, b21, b22, b3);

	// Older non-domestic certificates left meta.stages empty and kept the stage
	// description in meta.stagedDescription, so fall back to it rather than
	// indexing an empty array and printing "undefined" onto the certificate.
	const stages = certificate.meta.stages ?? [];
	const stageDescription =
		stages[stages.length - 1] ?? certificate.meta.stagedDescription;

	if (stageDescription) {
		b3 = drawCol("Stage Description", stageDescription, 2, 2 * colW, b3);
	}

	// Spans the full width from column 0, so it must start below the lowest of
	// ALL the columns - not just Description of Works - or it prints over a
	// wrapped Project Location and the Compliance Methods column.
	let b5 = Math.min(b1, b21, b22, b3);

	if (certificate.meta.amendments?.length > 0) {
		b5 = drawCol(
			"Amendment Description",
			certificate.meta.amendments[certificate.meta.amendments.length - 1],
			0,
			4 * colW,
			b5,
		);
	}

	y = Math.min(b1, b21, b22, b3, b5) - largeGap;

	const bullets = [
		"I certify that I have used reasonable skill, care and diligence in the consideration of this design for the project described in the application.",
		`The energy design complies with the Building (Scotland) Regulations 2004 (as amended) with respect to Standards 6.1 to 6.8 (${
			certificate.domestic ? "Domestic" : "Non-Domestic"
		}).`,
		"Where this certificate relates to an application for a stage, the design of this stage is consistent with the design of any previously certified stages.",
		"I am registered by the Scottish Government Building Standards Division as competent to certify the design of such work.",
		"The building, when converted in accordance with the warrant application, will comply with the requirements applicable to energy contained within Regulation 12 of the Building (Scotland) Regulations 2004 (as amended).",
	];
	const numerals = ["", "i", "ii", "iii", "iv"];

	const bulletWidth = standardWidth - lineGap;
	bullets.forEach((b, i) => {
		const lines = wrapText(regular, b, 8, bulletWidth);
		if (i > 0) {
			page.drawText(numerals[i], {
				x: xMargin + largeGap,
				y,
				size: 8,
				font: regular,
			});
		}
		let cy = y;
		for (const ln of lines) {
			page.drawText(ln, {
				x: i > 0 ? xMargin + largeGap + lineGap : xMargin + largeGap,
				y: cy,
				size: 8,
				font: regular,
			});
			cy -= 12;
			({ page, y: cy } = ensureSpace(pdf, page, cy));
		}
		y = cy - 2;
		({ page, y } = ensureSpace(pdf, page, y));
	});
	y -= lineGap;

	// Notes
	const notes = [
		"Note 1: This certificate has been issued in support of an application for building warrant under the Building (Scotland) Act 2003 only and must not be used or relied upon for any other purpose including under any contract to which the Certifier is not a party.",
		"Note 2: This certificate relates to the building regulations in force at the date the building warrant application is made.",
		"Note 3: This certificate relates solely to the design of the building (or the design up to the stage of construction to which the certificate relates) and does not certify any aspects of construction.",
		"Note 4: Any energy details which have yet to be designed in detail and for which a performance specification has been supplied with the building warrant application are listed in the Schedule 1 accompanying this certificate.",
	];

	for (const n of notes) {
		const lines = wrapText(regular, n, 6, standardWidth);
		for (const ln of lines) {
			page.drawText(ln, { x: xMargin + largeGap, y, size: 6, font: regular });
			y -= lineGap - mm(2);
			({ page, y } = ensureSpace(pdf, page, y));
		}
		y -= lineGap / 6;
	}

	const dottedLine = " . . . . . . . . . . . . . . . . . . . . . . . . ";
	const createSignCol = (sigData, dateData) => {
		const sx = xMargin + largeGap;
		let sw = regular.widthOfTextAtSize(`Signature ${dottedLine}`, 9);
		let dw = regular.widthOfTextAtSize(`Date ${dottedLine}`, 9);
		Object.keys(sigData).forEach(label => {
			if (sigData[label]) {
				const secW =
					bold.widthOfTextAtSize(label, 9) +
					italic.widthOfTextAtSize(sigData[label], 9) +
					mm(2);
				if (secW > sw) {
					sw = secW;
				}
			}
		});
		Object.keys(dateData).forEach(label => {
			if (dateData[label]) {
				const secW =
					bold.widthOfTextAtSize(label, 9) +
					italic.widthOfTextAtSize(dateData[label], 9) +
					mm(2);
				if (secW > dw) {
					dw = secW;
				}
			}
		});
		let remW = standardWidth - sw - dw;

		page.drawText(`Signature ${dottedLine}`, {
			x: sx + remW / 4,
			y: y,
			size: 9,
			font: regular,
		});

		let sy = y - lineGap;
		let dy = sy;
		Object.keys(sigData).forEach(label => {
			if (!sigData[label]) return;
			page.drawText(label, {
				x: sx + remW / 4,
				y: sy,
				size: 9,
				font: bold,
			});
			page.drawText(sigData[label], {
				x: sx + remW / 4 + bold.widthOfTextAtSize(label, 9) + mm(2),
				y: sy,
				size: 9,
				font: italic,
			});
			sy -= lineGap;
		});

		page.drawText(`Date ${dottedLine}`, {
			x: sx + (3 * remW) / 4 + sw,
			y: y,
			size: 9,
			font: regular,
		});
		Object.keys(dateData).forEach(label => {
			page.drawText(label, {
				x: sx + (3 * remW) / 4 + sw,
				y: dy,
				size: 9,
				font: bold,
			});
			page.drawText(dateData[label], {
				x: sx + (3 * remW) / 4 + sw + bold.widthOfTextAtSize(label, 9) + mm(2),
				y: dy,
				size: 9,
				font: regular,
			});
			dy -= lineGap;
		});

		return Math.min(y, sy, dy);
	};

	y -= lineGap;
	y = createSignCol(
		{ "Name:": certificate.subscription.name },
		{ "Certifier Registration No": certificate.subscription.regNo },
	);
	metaBox(topY, topY - y);
	y -= largeGap * 1.5;

	({ page, y } = ensureSpace(pdf, page, y, yMargin * 3));

	if (y < 155) {
		({ page, y } = ensureSpace(pdf, page, 0));
	}
	// PART B
	page.drawText(
		"PART B: To be completed by the Certification Coordinator of the Approved Body",
		{
			x: xMargin,
			y,
			size: 11,
			font: bold,
		},
	);
	y -= lineGap / 2;
	topY = y;
	y -= largeGap * 0.75;

	const ccText = `I confirm that the person signing Part A is an Approved Certifier of Design and is a principal in or is employed by this Approved Body. This body is registered by the Scottish Government Building Standards Division to provide certification services for Design (Section 6 – Energy) ${
		certificate.domestic ? "Domestic" : "Non-Domestic"
	} and employs at least one Approved Certifier under that scheme. I am registered as the certification coordinator for this body.`;
	const lines = wrapText(regular, ccText, 8, standardWidth);
	for (const ln of lines) {
		page.drawText(ln, { x: xMargin + largeGap, y, size: 8, font: regular });
		y -= lineGap - mm(1);
		({ page, y } = ensureSpace(pdf, page, y));
	}

	y -= largeGap;
	y = createSignCol(
		{
			"Name:": certificate.approvedBody.coordinatorName,
			"Approved Body:": certificate.approvedBody.name,
		},
		{ "Approved Body Registration No:": certificate.approvedBody.id },
	);
	metaBox(topY, topY - y);

	footer();

	if (certificate.status !== "paid") {
		drawWatermark();
	}
	const bytes = await pdf.save();
	/*
	const blob = new Blob([bytes], { type: "application/pdf" });
	const pdfUrl = URL.createObjectURL(blob);
	open(pdfUrl);*/
	return bytes;
};
